import { useEffect, useRef, useState } from 'preact/hooks';
import {
  getEdenOnboardingLink,
  activateEdenOnboarding,
  getEdenConsentTexts,
  grantEdenConsent,
  submitEdenProfileFact,
  submitEdenObservation,
  completeEdenOnboarding,
  mintEdenRealtimeSession,
  dispatchEdenRealtimeToolCall,
  type EdenSessionContext,
  type EdenConsentText,
  type EdenProfileFactKey,
} from '../lib/api';
import {
  connectEdenRealtimeVoice,
  type EdenRealtimeRelayResult,
  type EdenRealtimeSessionHandle,
} from '../lib/edenRealtimeClient';
import { Knot, type KnotState } from '../components/Knot';
import './EdenOnboarding.css';

// packages/web/src/screens/EdenOnboarding.tsx — V19b-2: the client's first
// contact with Eden (docs/console/v19b-the-client-surface.md). Route
// #/eden/onboarding/:token, matched BEFORE the auth/tenant gate in App.tsx
// — see that call site's own comment for why this sits with the reviewer/
// record-grant portals rather than behind Login.
//
// REUSED RATHER THAN REBUILT (the spec's own instruction, checked first):
//   - the pre-auth token-route pattern is ReviewerPortal's (App.tsx ~695);
//   - microphone acquisition + its refusal path follow ReviewerPortal's
//     own startRecording/mediaRecorderSupported precedent, adapted here
//     to a live-listening Knot rather than a fixed recording;
//   - the Knot component IS the Talk → Understand → See loop: a fact card
//     with a ✓ and an edit affordance is a Knot proposal the client
//     approves (by leaving it) or corrects (by editing it) — this file
//     does not build a parallel confirm dialog.
//
// WHAT THIS FILE DOES NOT BUILD (out of this chunk's remit, named so a
// reviewer never mistakes silence for an oversight):
//   - V19b-1's four new routes (consent-texts/consent/profile-fact/
//     observation) — this file only codes against their documented
//     contract (lib/api.ts's own header on the point);
//   - V19b-3's realtime voice client: the actual WebRTC session against
//     OpenAI (SDP exchange, the data channel, tool-call relay to
//     /api/eden/realtime/tool-calls). What IS built here is the mic
//     PERMISSION gate and a locally-analysed listening Knot (Web Audio
//     only, no network) — see `useMicrophone` below. The `listening`
//     Knot state is exactly where V19b-3 attaches the real session; until
//     it does, the typed composer is how the client actually finishes
//     (INV-19B-4), which this chunk mutation-tests directly.

export interface EdenOnboardingProps {
  token: string;
  /** The deployment's product name — see PortalFrame's own doc comment on
   * why `null` (still loading) must never fall back to a guess. */
  brand?: string | null;
}

const PROFILE_FACT_LABELS: Record<EdenProfileFactKey, string> = {
  full_name: 'Name',
  preferred_name: 'Goes by',
  company_name: 'Company',
  company_size: 'Team size',
  company_does: 'What they do',
  role: 'Role',
  email: 'Email',
  phone: 'Phone',
  notes: 'Note',
};

const EMAIL_KEY = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FactCard = {
  kind: 'profile_fact';
  id: string;
  key: EdenProfileFactKey;
  value: string;
  receipt: string;
  consentGranted?: boolean;
};
type ObservationCard = {
  kind: 'observation';
  id: string;
  body: string;
  ticketRef: string;
  receipt: string;
};
type Card = FactCard | ObservationCard;

type Phase =
  | { kind: 'loading' }
  | { kind: 'invalid'; message: string }
  | { kind: 'expired'; message: string }
  | { kind: 'ready'; session: EdenSessionContext }
  | { kind: 'done'; partyName: string; receipts: string[] };

type Step = 'email' | 'colleagues' | 'completing';

let cardSeq = 0;
function nextCardId(): string {
  cardSeq += 1;
  return `card-${cardSeq}`;
}

// ⚑ A MISSING ENTRY HERE IS SILENT: the type is `readonly EdenProfileFactKey[]`,
// so a SUBSET still typechecks, and isEdenProfileFactKey would simply return
// false — the model's capture would be dropped on the floor with no error
// anywhere. Every key in PROFILE_FACT_KEYS must appear.
const EDEN_PROFILE_FACT_KEYS: readonly EdenProfileFactKey[] = [
  'full_name',
  'preferred_name',
  'company_name',
  'company_size',
  'company_does',
  'role',
  'email',
  'phone',
  'notes',
];
function isEdenProfileFactKey(value: unknown): value is EdenProfileFactKey {
  return typeof value === 'string' && (EDEN_PROFILE_FACT_KEYS as readonly string[]).includes(value);
}

/** The tool-call dispatcher's own 'executed' receipt for capture_observation
 * (V18b's captureObservation) always NAMES the ticket ref it just made —
 * "Captured as ticket VW-…" / "Ticket VW-…: captured…" — so the ref is
 * read back from the server's own sentence rather than re-derived, the
 * same posture the V19b-2 walk's own receipt assertions take. */
/** The CALL's ticket, which is the only reference a client is ever shown
 *  (Huan, 2026-09-18: "just one for the entire thing for the call").
 *
 *  ⚑ Any item suffix is dropped ON PURPOSE. A server receipt names the item —
 *  VW-01a0b785-1.1 — because that is what the team works; the client hears the
 *  conversation's number. This previously produced the right string by
 *  ACCIDENT, because the old character class happened to exclude '.', which is
 *  the kind of correctness that silently reverses the first time someone
 *  widens a regex. */
function extractTicketRef(receipt: string): string | null {
  const m = receipt.match(/VW-[A-Za-z0-9]+-\d+/);
  return m ? m[0] : null;
}

/** Maps the tool-call dispatcher's wire outcome (lib/api.ts's
 * EdenRealtimeToolCallOutcome — carries a `replay` flag the relay caller
 * has no use for) onto edenRealtimeClient's narrower EdenRealtimeRelayResult
 * shape. This is the ONLY place a model tool-call name is ever touched on
 * this screen — and it does not inspect the name at all, only the
 * server's own status (INV-EDEN-5: the browser never maps a tool name to
 * an action itself). */
async function relayEdenRealtimeToolCall(
  token: string,
  call: { clientToolCallId: string; name: string; arguments: unknown }
): Promise<EdenRealtimeRelayResult> {
  const outcome = await dispatchEdenRealtimeToolCall(token, call);
  switch (outcome.status) {
    case 'executed':
      return { status: 'executed', receipt: outcome.receipt };
    case 'refused':
      return { status: 'refused', reason: outcome.reason };
    case 'budget_exceeded':
      return { status: 'budget_exceeded', receipt: outcome.receipt };
    case 'invalid':
      return { status: 'invalid' };
    case 'expired':
      return { status: 'expired' };
  }
}

function microphoneSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  );
}

/** Mic permission + a LOCAL (Web Audio, no network) amplitude reader — the
 * refusal path ReviewerPortal's own startRecording already proves out,
 * adapted so the Knot pulses with the real voice level while listening.
 * Deliberately does not open a WebRTC session or send audio anywhere
 * (V19b-3's job) — this hook's entire contract ends at "the mic is
 * live and here is its amplitude, 0..1". */
function useMicrophone(): {
  state: 'idle' | 'requesting' | 'listening' | 'denied' | 'unavailable';
  amp: number;
  start: () => Promise<MediaStream | null>;
  stop: () => void;
} {
  const supported = microphoneSupported();
  const [state, setState] = useState<'idle' | 'requesting' | 'listening' | 'denied' | 'unavailable'>(
    supported ? 'idle' : 'unavailable'
  );
  const [amp, setAmp] = useState(0);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number>(0);

  function stop() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    void audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
    setAmp(0);
    setState((s) => (s === 'listening' || s === 'requesting' ? 'idle' : s));
  }

  async function start(): Promise<MediaStream | null> {
    if (!supported || state === 'listening' || state === 'requesting') return null;
    setState('requesting');
    try {
      // ⚑ ECHO CANCELLATION IS NOT OPTIONAL HERE, and bare `audio: true` was
      // what Huan met: on a phone's loudspeaker Eden hears ITSELF, the
      // server's voice-activity detection reads that as the client speaking,
      // and it answers its own greeting -- over and over, which is exactly
      // what he described. The same signature showed up in our own live walk
      // as 22 responses to repeated audio.
      //
      // Asked for explicitly rather than left to the browser's default: the
      // default is not guaranteed, and on Android Chrome with a speaker it is
      // precisely the case that goes wrong.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      setState('listening');

      const Ctx = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
        .AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        audioCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteTimeDomainData(data);
          let sumSquares = 0;
          for (let i = 0; i < data.length; i++) {
            const v = (data[i]! - 128) / 128;
            sumSquares += v * v;
          }
          setAmp(Math.min(1, Math.sqrt(sumSquares / data.length) * 4));
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      }
      return stream;
    } catch {
      setState('denied');
      return null;
    }
  }

  useEffect(() => () => stop(), []);

  return { state, amp, start, stop };
}

export function EdenOnboarding({ token, brand }: EdenOnboardingProps) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const linkOutcome = await getEdenOnboardingLink(token);
      if (cancelled) return;
      if (linkOutcome.status === 'invalid') {
        setPhase({ kind: 'invalid', message: linkOutcome.message ?? 'This link is no longer active.' });
        return;
      }
      if (linkOutcome.status === 'expired') {
        setPhase({ kind: 'expired', message: linkOutcome.message ?? 'This link has expired.' });
        return;
      }
      const activated = await activateEdenOnboarding(token);
      if (cancelled) return;
      if (activated.status !== 'open') {
        setPhase(
          activated.status === 'expired'
            ? { kind: 'expired', message: activated.message ?? 'This link has expired.' }
            : { kind: 'invalid', message: activated.message ?? 'This link is no longer active.' }
        );
        return;
      }
      setPhase({ kind: 'ready', session: activated });
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (phase.kind === 'loading') {
    return (
      <EdenFrame brand={brand}>
        <div class="eden__soft">One moment…</div>
      </EdenFrame>
    );
  }
  if (phase.kind === 'invalid') {
    return (
      <EdenFrame brand={brand}>
        <div class="eden__lead">{phase.message}</div>
        <div class="eden__soft">Ask whoever sent this for a fresh link.</div>
      </EdenFrame>
    );
  }
  if (phase.kind === 'expired') {
    return (
      <EdenFrame brand={brand}>
        <div class="eden__lead">{phase.message}</div>
        <div class="eden__soft">Ask whoever sent this for a fresh one.</div>
      </EdenFrame>
    );
  }
  if (phase.kind === 'done') {
    return (
      <EdenFrame brand={brand}>
        <div class="eden__lead">{phase.partyName}, you're set.</div>
        <ul class="eden__receipts" data-testid="final-receipts">
          {phase.receipts.map((r, i) => (
            <li key={i} class="eden__receipt">
              {r}
            </li>
          ))}
        </ul>
      </EdenFrame>
    );
  }

  return (
    <OnboardingConversation
      token={token}
      brand={brand}
      session={phase.session}
      onDone={(receipts) => setPhase({ kind: 'done', partyName: phase.session.partyName, receipts })}
    />
  );
}

function EdenFrame({ children, brand }: { children: preact.ComponentChildren; brand?: string | null }) {
  return (
    <div class="eden">
      <div class="eden__brand">{brand ?? ' '}</div>
      <div class="eden__card">{children}</div>
    </div>
  );
}

interface OnboardingConversationProps {
  token: string;
  brand?: string | null;
  session: EdenSessionContext;
  onDone: (receipts: string[]) => void;
}

function OnboardingConversation({ token, brand, session, onDone }: OnboardingConversationProps) {
  const [consentTexts, setConsentTexts] = useState<EdenConsentText[] | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [step, setStep] = useState<Step>('email');
  const [composerValue, setComposerValue] = useState('');
  const [emailConsent, setEmailConsent] = useState(false);
  const [capturedEmail, setCapturedEmail] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [voiceNote, setVoiceNote] = useState<string | null>(null);
  /** Has the client begun? Before this the screen is the greeting and the
   *  Knot ONLY. Set the moment they choose either door -- including a voice
   *  attempt that goes on to fail, so a mic refusal lands them in the full
   *  typed screen rather than back at an arrival that just rejected them. */
  const [started, setStarted] = useState(false);
  /** ⚑ Is the typing box open? (Huan, 2026-09-18: "when we tap on the knot it
   *  shouldn't open up the capture box, it should just start the process...
   *  we should have something at the bottom that they can click to open a
   *  capture window.") Tapping the Knot starts the CONVERSATION; the box is a
   *  separate, deliberate act -- and Eden itself mentions it once the
   *  conversation is going, which is the teaching. */
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const mic = useMicrophone();
  const secretRef = useRef<string | null>(null); // INV-19B-8: held, never rendered.
  // The live WebRTC session, if one is open — held in a ref (never
  // rendered) the same way secretRef is. A ref, not state: opening/closing
  // it must never itself trigger a re-render, and every reader of it is an
  // event callback, not JSX.
  const realtimeSessionRef = useRef<EdenRealtimeSessionHandle | null>(null);
  const [voicePulse, setVoicePulse] = useState<'idle' | 'proposing' | 'confirmed'>('idle');
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getEdenConsentTexts(token).then((outcome) => {
      if (cancelled) return;
      if (outcome.status === 'open') setConsentTexts(outcome.texts);
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Leaving this screen (unmount) must never leave a live voice session
  // open behind it — INV-EDEN-11's "never a silent hang" cuts both ways.
  useEffect(() => {
    return () => {
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
      realtimeSessionRef.current?.close();
    };
  }, []);

  const emailConsentText = consentTexts?.find((t) => t.kind === 'contact.email') ?? null;

  function pulseConfirmed() {
    setVoicePulse('confirmed');
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = setTimeout(() => setVoicePulse('idle'), 900);
  }

  /** A tool call the SERVER executed — this is the ONLY place a voice
   *  capture becomes a Knot proposal on screen, and it only runs after the
   *  server's own receipt comes back, never on the model's say-so alone.
   *  Renders through the exact same card state the typed door writes to
   *  (addFactCard / the observation branch below) — no parallel confirm
   *  flow, per this chunk's own brief. */
  function handleVoiceToolExecuted(call: { name: string; arguments: unknown; receipt: string }) {
    const a = (call.arguments && typeof call.arguments === 'object' ? call.arguments : {}) as Record<string, unknown>;
    if (call.name === 'record_profile_fact') {
      const key = a.key;
      const value = a.value;
      if (isEdenProfileFactKey(key) && typeof value === 'string' && value.trim()) {
        addFactCard(key, value.trim(), call.receipt);
        pulseConfirmed();
        if (key === 'email') setCapturedEmail(value.trim());
      }
    } else if (call.name === 'capture_observation') {
      const body = typeof a.body === 'string' ? a.body.trim() : '';
      if (body) {
        const ticketRef = extractTicketRef(call.receipt) ?? call.receipt;
        setCards((prev) => [...prev, { kind: 'observation', id: nextCardId(), body, ticketRef, receipt: call.receipt }]);
      }
    } else if (call.name === 'request_typed_input') {
      const field = typeof a.field === 'string' && a.field.trim() ? a.field.trim() : 'that';
      setVoiceNote(`Go ahead and type ${field} below.`);
    }
    pulseConfirmed();
  }

  /** A tool call the server REFUSED — honest and calm (never a crash, never
   *  silently dropped): named right where the client can see it, and the
   *  typed door is untouched either way. */
  function handleVoiceToolRefused(call: { name: string; reason: string }) {
    setVoiceNote(call.reason);
  }

  /** The voice session is over, for any reason (INV-EDEN-11: always named,
   *  never a silent hang). Everything already captured — in `cards` state —
   *  is untouched; only the mic/session tear down, and the typed composer
   *  (never disabled by voice state) is how the client keeps going. */
  function handleVoiceEnded(reason: 'budget_exceeded' | 'invalid' | 'expired' | 'dropped' | 'closed', receipt?: string) {
    realtimeSessionRef.current = null;
    mic.stop();
    if (reason === 'budget_exceeded') {
      setVoiceNote(receipt ?? "This session's voice budget has been reached — you can still type below.");
    } else if (reason === 'invalid' || reason === 'expired') {
      setVoiceNote('That link stopped working for voice — everything captured so far is kept. You can still type below.'); setComposerOpen(true);
    } else if (reason === 'dropped') {
      setVoiceNote('The voice connection dropped — everything captured so far is kept. You can still type below.'); setComposerOpen(true);
    }
    // 'closed' — the client hung up on purpose (pressed Talk again); no
    // note needed, the Knot simply returns to idle.
  }

  async function handleTalk() {
    if (mic.state === 'listening') {
      realtimeSessionRef.current?.close();
      mic.stop();
      return;
    }
    const stream = await mic.start();
    if (!stream) { setComposerOpen(true); return; } // denied/unavailable — open the box in the same act (INV-19B-4)

    // Exercises the real V18c mint seam. A refusal here (budget/invalid/
    // expired) is named and calm — the typed composer below is untouched
    // either way (INV-19B-4).
    const mint = await mintEdenRealtimeSession(token);
    if (mint.status === 'budget_exceeded') {
      setVoiceNote(mint.receipt);
      mic.stop();
      return;
    }
    if (mint.status !== 'open') {
      setVoiceNote('That link stopped working for voice — you can still type below.'); setComposerOpen(true);
      mic.stop();
      return;
    }
    secretRef.current = mint.ephemeralSecret; // never read into JSX
    setVoiceNote(null);

    // The actual WebRTC handshake — see lib/edenRealtimeClient.ts. A
    // failure here (network error, a non-2xx from OpenAI) is caught, named
    // calmly, and NEVER blocks the typed door (INV-19B-4/INV-EDEN-4): the
    // mic simply stops and the client keeps going by typing.
    try {
      realtimeSessionRef.current = await connectEdenRealtimeVoice(
        { ephemeralSecret: mint.ephemeralSecret, model: mint.model, stream, relay: (call) => relayEdenRealtimeToolCall(token, call) },
        {
          onToolExecuted: handleVoiceToolExecuted,
          onToolRefused: handleVoiceToolRefused,
          onConnected: () => setVoiceNote(null),
          // If the browser refuses to play Eden's voice the session is
          // otherwise healthy -- so nothing else would ever tell the client
          // why they are sitting in silence. Name it, and point at the door
          // that still works.
          onVoicePlaybackBlocked: () =>
            setVoiceNote("Your browser blocked Eden's voice — tap Talk again, or just type below."),
          onEnded: handleVoiceEnded,
        }
      );
    } catch {
      setVoiceNote('Voice could not connect — you can still type below.');
      setComposerOpen(true);
      mic.stop();
    }
  }

  /** ⚑ A PROFILE FACT IS SINGULAR PER KEY, so a new capture for a key that
   *  already has a card REPLACES it — it does not stack up beside it.
   *
   *  Huan, 2026-09-18: "I don't like the fact that it can't correct things for
   *  you. You have to click the Correct button and do it yourself." This
   *  appended, so saying "no, it's Huan" gave him a SECOND name card rather
   *  than a corrected one — and the database showed the same preferred_name
   *  written three times over. INV-19B-6 says a mishearing is correctable in
   *  one action "by voice OR by touch"; only touch worked, and the screen was
   *  half the reason.
   *
   *  Observations are NOT this: several are genuinely several, so they append. */
  function addFactCard(key: EdenProfileFactKey, value: string, receipt: string, consentGranted?: boolean) {
    setCards((prev) => {
      const at = prev.findIndex((c) => c.kind === 'profile_fact' && c.key === key);
      const card = { kind: 'profile_fact' as const, id: at >= 0 ? prev[at]!.id : nextCardId(), key, value, receipt, consentGranted };
      if (at < 0) return [...prev, card];
      const next = [...prev];
      next[at] = card;
      return next;
    });
  }

  async function correctFact(card: FactCard, newValue: string) {
    const trimmed = newValue.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      const outcome = await submitEdenProfileFact(token, { key: card.key, value: trimmed, capturedVia: 'typed' });
      if (outcome.status !== 'open') {
        setError(outcome.message ?? 'That link stopped working — refresh and try again.');
        return;
      }
      setCards((prev) =>
        prev.map((c) => (c.id === card.id ? { ...c, value: trimmed, receipt: outcome.receipt } : c))
      );
      if (card.key === 'email') setCapturedEmail(trimmed);
      setEditingCardId(null);
    } finally {
      setBusy(false);
    }
  }

  async function submitEmailStep() {
    const value = composerValue.trim();
    if (!EMAIL_KEY.test(value)) {
      setError('That doesn’t look like an email address yet.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      let consentGranted = false;
      // INV-19B-2: the grant runs in the SAME action as the field it
      // governs, before the fact is even written — never a step added
      // afterwards.
      if (emailConsent && emailConsentText) {
        const grant = await grantEdenConsent(token, [
          { kind: 'contact.email', textVersion: emailConsentText.version },
        ]);
        if (grant.status === 'open') consentGranted = true;
      }
      const fact = await submitEdenProfileFact(token, { key: 'email', value, capturedVia: 'typed' });
      if (fact.status !== 'open') {
        setError(fact.message ?? 'That link stopped working — refresh and try again.');
        return;
      }
      addFactCard('email', value, fact.receipt, consentGranted);
      setCapturedEmail(value);
      setComposerValue('');
      setStep('colleagues');
    } finally {
      setBusy(false);
    }
  }

  async function submitColleaguesStep(skip: boolean) {
    const value = composerValue.trim();
    if (skip || !value) {
      setComposerValue('');
      void finishOnboarding([]);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const outcome = await submitEdenObservation(token, { kind: 'direction', body: value, capturedVia: 'typed' });
      if (outcome.status !== 'open') {
        setError(outcome.message ?? 'That link stopped working — refresh and try again.');
        return;
      }
      setCards((prev) => [
        ...prev,
        { kind: 'observation', id: nextCardId(), body: value, ticketRef: outcome.ticketRef, receipt: outcome.receipt },
      ]);
      setComposerValue('');
      // `cards` state from THIS closure does not yet include the row just
      // added above — setCards schedules its re-render, it does not apply
      // synchronously — so the observation's own receipt is passed through
      // explicitly rather than trusted to `cards` by the time
      // finishOnboarding reads it. Found by the real-browser walk: the
      // ticket was captured for real (present in the database and on the
      // card) but silently missing from the final receipt list.
      void finishOnboarding([outcome.receipt]);
    } finally {
      setBusy(false);
    }
  }

  /** `pendingReceipts` — receipts from an action taken in the SAME turn as
   *  this call, not yet reflected in `cards` state (see submitColleaguesStep
   *  above). Always included ahead of `cards`' own receipts and the
   *  completion receipt, so nothing captured this session is ever dropped
   *  from what the client is shown at the end (INV-19B-7). */
  async function finishOnboarding(pendingReceipts: string[]) {
    // Completion is the end of THIS screen's job — a voice session left
    // open into the receipt screen is exactly the "silent hang" INV-EDEN-11
    // forbids, even though nothing here failed.
    realtimeSessionRef.current?.close();
    mic.stop();
    setStep('completing');
    setBusy(true);
    setError(null);
    const email = capturedEmail;
    if (!email) {
      setError('An email is needed to finish — add one above.');
      setStep('email');
      setBusy(false);
      return;
    }
    try {
      const outcome = await completeEdenOnboarding(token, { email });
      if (outcome.status !== 'open') {
        setError(outcome.message ?? 'That link stopped working — refresh and try again.');
        setStep('colleagues');
        return;
      }
      onDone([...cards.map((c) => c.receipt), ...pendingReceipts, outcome.receipt]);
    } finally {
      setBusy(false);
    }
  }

  function onComposerSubmit(e: Event) {
    e.preventDefault();
    if (busy) return;
    if (step === 'email') void submitEmailStep();
    else if (step === 'colleagues') void submitColleaguesStep(false);
  }

  // ⚑ VOICE-LED ARRIVAL (Huan, 2026-09-18): "They should be greeted. It turns
  // on the mic and the speaker. And the first thing is that they hear Eden
  // talking to them, so they know right away that they're supposed to speak
  // back." What shipped instead put the Knot INSIDE the email form, beside a
  // labelled input and a Send button -- so the page read as "fill this in",
  // which is what Huan did, and the voice never got a chance to teach him
  // anything.
  //
  // A browser will not open a microphone or play audio without one user
  // gesture (iOS Safari most strictly -- and iOS is the stated bar), so
  // "turns on by itself" is not reachable. The nearest honest thing, and what
  // this builds: the gesture is the ONLY thing on the arrival screen, and
  // everything after it is voice.
  // ⚑ `mic.state === 'unavailable'` means the browser has NO media API at all.
  // Offering a Knot there would be an affordance that cannot work -- so the
  // voice-led arrival is skipped entirely and the client lands directly on the
  // typed door. INV-19B-4: never a dead end, and never a button that lies.
  const arrived = !started && step === 'email' && cards.length === 0 && mic.state !== 'unavailable';

  const knotState: KnotState =
    voicePulse === 'confirmed'
      ? 'confirmed'
      : mic.state === 'listening'
        ? 'listening'
        : mic.state === 'requesting'
          ? 'understanding'
          : 'idle';

  return (
    <EdenFrame brand={brand}>
      <div class="eden__lead">{session.resumed ? `Welcome back, ${session.partyName}.` : `Hi, ${session.partyName}.`}</div>

      {arrived && (
        <div class="eden__arrival">
          <button
            type="button"
            class="eden__arrival-knot"
            data-testid="talk-button"
            onClick={() => { setStarted(true); void handleTalk(); }}  // conversation only — never the box
            aria-label="Start talking with Eden"
          >
            <span class="eden__arrival-breath">
              <Knot state={knotState} amp={mic.amp} size={128} />
            </span>
          </button>
          {/* INV-19B-4: the typed door stays first-class and reachable
              without ever attempting voice -- a quiet way through, not a
              fallback banner and not an instructional caption. */}
          <button
            type="button"
            class="eden__arrival-type"
            data-testid="arrival-type-instead"
            onClick={() => { setStarted(true); setComposerOpen(true); }}
          >
            Type instead
          </button>
        </div>
      )}

      {cards.length > 0 && (
        <ul class="eden__cards" data-testid="fact-cards">
          {cards.map((card) => (
            <li key={card.id} class="eden__card-row">
              {card.kind === 'profile_fact' ? (
                editingCardId === card.id ? (
                  <form
                    class="eden__card-edit"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void correctFact(card, editValue);
                    }}
                  >
                    <input
                      class="eden__card-edit-input"
                      value={editValue}
                      onInput={(e) => setEditValue((e.target as HTMLInputElement).value)}
                      autoFocus
                    />
                    <button type="submit" class="eden__card-save" disabled={busy}>
                      Save
                    </button>
                    <button type="button" class="eden__card-cancel" onClick={() => setEditingCardId(null)}>
                      Cancel
                    </button>
                  </form>
                ) : (
                  <>
                    <span class="eden__card-check" aria-hidden="true">
                      ✓
                    </span>
                    <span class="eden__card-label">{PROFILE_FACT_LABELS[card.key]}</span>
                    <span class="eden__card-value" data-testid={`fact-value-${card.key}`}>
                      {card.value}
                    </span>
                    {card.consentGranted && <span class="eden__card-tag">may email</span>}
                    <button
                      type="button"
                      class="eden__card-edit-btn"
                      data-testid={`fact-correct-${card.key}`}
                      onClick={() => {
                        setEditingCardId(card.id);
                        setEditValue(card.value);
                      }}
                    >
                      Correct
                    </button>
                  </>
                )
              ) : (
                <>
                  <span class="eden__card-check" aria-hidden="true">
                    ✓
                  </span>
                  <span class="eden__card-label">Ticket {card.ticketRef}</span>
                  <span class="eden__card-value">{card.body}</span>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {step !== 'completing' && !arrived && composerOpen && (
        <div class="eden__ask">
          {step === 'email' && (
            <>
              <label class="eden__ask-label" for="eden-composer">
                Your email
              </label>
              {emailConsentText && (
                <label class="eden__consent">
                  <input
                    type="checkbox"
                    checked={emailConsent}
                    data-testid="consent-email-checkbox"
                    onChange={(e) => setEmailConsent((e.target as HTMLInputElement).checked)}
                  />
                  <span data-testid="consent-email-text">{emailConsentText.body}</span>
                </label>
              )}
            </>
          )}
          {step === 'colleagues' && (
            <label class="eden__ask-label" for="eden-composer">
              Anyone else on your team who should have access?
            </label>
          )}
        </div>
      )}

      {error && (
        <div class="eden__error" data-testid="eden-error">
          {error}
        </div>
      )}
      {voiceNote && <div class="eden__voice-note">{voiceNote}</div>}
      {mic.state === 'denied' && <div class="eden__voice-note">Microphone permission was declined.</div>}

      {arrived ? null : step !== 'completing' && !composerOpen ? (
        // The conversation is running and the box is shut: one quiet way to
        // open it, at the bottom, where Eden says it is.
        <button
          type="button"
          class="eden__open-composer"
          data-testid="open-composer"
          onClick={() => setComposerOpen(true)}
        >
          Type something
        </button>
      ) : step !== 'completing' ? (
        <form class="eden__composer" onSubmit={onComposerSubmit}>
          {mic.state !== 'unavailable' && (
            <button
              type="button"
              class="eden__talk"
              data-testid="talk-button"
              onClick={() => void handleTalk()}
              aria-pressed={mic.state === 'listening'}
            >
              <Knot state={knotState} amp={mic.amp} size={32} />
            </button>
          )}
          <input
            id="eden-composer"
            class="eden__composer-input"
            data-testid="composer-input"
            value={composerValue}
            placeholder={step === 'email' ? 'you@example.com' : 'Type a name or email…'}
            onInput={(e) => setComposerValue((e.target as HTMLInputElement).value)}
            disabled={busy}
          />
          <button type="submit" class="eden__composer-send" data-testid="composer-send" disabled={busy}>
            {busy ? '…' : 'Send'}
          </button>
          {step === 'colleagues' && (
            <button
              type="button"
              class="eden__composer-skip"
              data-testid="composer-skip"
              onClick={() => void submitColleaguesStep(true)}
              disabled={busy}
            >
              Not now
            </button>
          )}
        </form>
      ) : (
        <div class="eden__soft">One moment…</div>
      )}
    </EdenFrame>
  );
}
